import { Collapse } from '@chakra-ui/react'
import React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { useHasRole } from '../../helpers/index'
import { Icon } from '../Icon'
import { AiSparkleIcon } from '../icons'
import { navbarItems } from './navbarItems'
import SpotlightSearch from './SpotlightSearch'
import './Sidebar.css'

export default function Sidebar() {
  const [sidebarActive, setSidebarActive] = useState(false)
  const [activeItem, setActiveItem] = useState<number | null>(null)
  // Grupo escolhido no último clique de subitem; o efeito de rota ainda não viu o novo pathname
  // quando ele é gravado, então fica num ref para não disparar o efeito antes da hora.
  const clickedItemRef = useRef<number | null>(null)
  const [sidebarItemOpen, setSidebarItemOpen] = useState<number | null>(null)
  const hasSystemRole = useHasRole()
  const { pathname } = useLocation()

  const openSpotlight = () => window.dispatchEvent(new CustomEvent('open-spotlight'))

  // `roles: []` significa visível para todos (contrato de 'navbarItems'), mas `hasSystemRole` devolve
  // false para lista vazia — daí a regra viver aqui, e não repetida em cada filtro.
  const isVisible = useCallback((roles: string[]) => roles.length === 0 || hasSystemRole(roles), [hasSystemRole])

  // O que a sidebar realmente renderiza, com grupos e subitens já filtrados por permissão.
  // `activeItem` e `sidebarItemOpen` guardam índices desta lista: indexar `navbarItems` inteiro
  // deslocava o destaque para o grupo errado sempre que um grupo anterior ficava escondido.
  const visibleItems = useMemo(
    () =>
      navbarItems
        .filter((item) =>
          'items' in item ? isVisible(item.items.flatMap((child) => child.roles)) : isVisible(item.roles)
        )
        .map((item) =>
          'items' in item ? { ...item, items: item.items.filter((subItem) => isVisible(subItem.roles)) } : item
        ),
    [isVisible]
  )

  useEffect(() => {
    const ownsPathname = (index: number | null) => {
      if (index === null || index < 0 || !visibleItems[index]) return false
      const item = visibleItems[index]
      return 'items' in item ? item.items.some((subItem) => subItem.path === pathname) : item.path.includes(pathname)
    }

    // Uma página pode estar em mais de um grupo: vale o grupo de onde ela foi aberta. Só caímos no
    // primeiro grupo que a contém quando se chega por fora da sidebar (URL direta, link, busca).
    const matchedIndex = ownsPathname(clickedItemRef.current)
      ? (clickedItemRef.current as number)
      : visibleItems.findIndex((_, index) => ownsPathname(index))
    clickedItemRef.current = matchedIndex

    setActiveItem(matchedIndex)

    if (matchedIndex !== -1 && 'items' in visibleItems[matchedIndex]) {
      setSidebarItemOpen(matchedIndex)
    }
  }, [pathname, visibleItems])

  return (
    <>
      <SpotlightSearch />
      <div
        className={`sidebar ${sidebarActive ? 'open' : ''}`}
        onMouseEnter={() => {
          setSidebarActive(true)
        }}
        onMouseLeave={() => {
          setSidebarActive(false)
        }}
      >
        <div className="sidebar-menu">
          <div style={{ textAlign: 'center', marginTop: '0.5rem', marginBottom: '1.5rem' }}>
            <img src={sidebarActive ? '/img/logo.png' : '/img/logo-mini.png'} className="sidebar-logo" alt="ABRA" />
          </div>
          {visibleItems.map((item, index) => {
            if ('path' in item) {
              return (
                <Link
                  to={item.path}
                  key={`navbar-item-${index}`}
                  className={`item ${activeItem === index ? 'active' : ''}`}
                  onClick={() => {
                    clickedItemRef.current = index
                    setActiveItem(index)
                    setSidebarItemOpen(null)
                  }}
                >
                  {item.icon === 'custom:ai-sparkle' ? (
                    <AiSparkleIcon style={{ marginRight: 16 }} />
                  ) : (
                    <Icon name={item.icon ?? 'lni lni-grid-alt'} />
                  )}
                  <span className="item-label">{item.title}</span>
                </Link>
              )
            }

            const toggleGroup = () => {
              setSidebarItemOpen(sidebarItemOpen === index ? null : index)
            }

            return (
              <React.Fragment key={`group-${index}`}>
                <div
                  className={`item ${activeItem === index ? 'active' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={sidebarItemOpen === index && sidebarActive}
                  aria-label={item.title}
                  onClick={toggleGroup}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggleGroup()
                    }
                  }}
                >
                  {item.icon === 'custom:ai-sparkle' ? (
                    <AiSparkleIcon style={{ marginRight: 16 }} />
                  ) : (
                    <Icon name={item.icon ?? 'lni lni-grid-alt'} />
                  )}
                  <span className="item-label">{item.title}</span>
                  <Icon name={`lni lni-chevron-down ${sidebarItemOpen === index ? 'open' : ''}`} />
                </div>
                <Collapse in={sidebarItemOpen === index && sidebarActive} animateOpacity>
                  {item.items.map((subItem, subIndex) => {
                    return (
                      <Link
                        to={subItem.path}
                        key={`subitem-${index}-${subIndex}`}
                        className={`subitem ${activeItem === index && subItem.path === pathname ? 'active' : ''}`}
                        onClick={() => {
                          clickedItemRef.current = index
                          setActiveItem(index)
                        }}
                      >
                        <span className="subitem-label">{subItem.title}</span>
                      </Link>
                    )
                  })}
                </Collapse>
              </React.Fragment>
            )
          })}
        </div>
        <div
          className="sidebar-footer item"
          role="button"
          tabIndex={0}
          aria-label="Buscar menus (atalho: /)"
          onClick={openSpotlight}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openSpotlight()
            }
          }}
        >
          <Icon name="lni lni-search-alt" />
          {sidebarActive && (
            <span className="sidebar-footer-label">
              Buscar menus
              <kbd className="sidebar-shortcut-key">/</kbd>
            </span>
          )}
        </div>
      </div>
    </>
  )
}
